import { api } from "../api.js";
import { AuthImage } from "../ui.jsx";
import { FlowWordmark } from "./Logo.jsx";

/**
 * The letterhead logo on an invoice or estimate.
 *
 * Pass the *issuer* — `doc.issuer || organization || settings` — not the live
 * organisation. A sent document freezes its letterhead into `issuer`, logo key
 * included, so a reprint shows what the client actually received rather than
 * today's branding.
 *
 * This lives in one place because it previously didn't, and the two pages drifted
 * in opposite directions: InvoiceDetail rendered the stored logo but showed
 * nothing when its bytes were missing, while EstimateDetail never passed a logo at
 * all and so always showed the built-in lockup even when a real logo existed. Both
 * disagreed with what their own PDF drew.
 *
 * The `fallback` is the point of the whole component: when the bytes can't be
 * loaded, show the same lockup `netlify/lib/pdf.mjs` falls back to, so the screen
 * and the PDF always tell the same story.
 */
export default function IssuerLogo({ issuer, live, height = 26 }) {
  const style = { maxWidth: 150, maxHeight: 46, objectFit: "contain", display: "block" };
  const wordmark = <FlowWordmark height={height} tone="light" />;

  const snapshotKey = issuer?.logo_blob_key;
  const liveKey = live?.logo_blob_key;

  // Mirrors logoFor() in netlify/lib/queries.mjs: the frozen letterhead first, the
  // organisation's current logo if those bytes have gone, and only then the
  // built-in wordmark. Nested so the second attempt happens when the first 404s.
  const liveAttempt =
    liveKey && liveKey !== snapshotKey ? (
      <AuthImage path={api.filePath(liveKey)} alt="" style={style} fallback={wordmark} />
    ) : (
      wordmark
    );

  if (!snapshotKey) return liveAttempt;
  return <AuthImage path={api.filePath(snapshotKey)} alt="" style={style} fallback={liveAttempt} />;
}
